<script setup lang="ts">
/**
 * AI视频创作平台 - 主布局组件
 * 包含顶部导航栏和主内容区域
 * 支持移动端响应式布局
 */
import { ref, watch, onMounted, onUnmounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import {
  Layout,
  Menu,
  Button,
  Avatar,
  Dropdown,
  Badge,
  Space,
  Drawer,
  Modal,
  message
} from 'ant-design-vue';
import {
  HomeOutlined,
  AppstoreOutlined,
  PictureOutlined,
  GlobalOutlined,
  UserOutlined,
  LogoutOutlined,
  WalletOutlined,
  PlusOutlined,
  MenuOutlined,
  CloseOutlined,
  MessageOutlined,
  HistoryOutlined,
  CustomerServiceOutlined,
  TeamOutlined,
  OrderedListOutlined,
  EditOutlined,
  MobileOutlined,
} from '@ant-design/icons-vue';
import { useUserStore } from '@/stores/user';
import { useLoginModal } from '@/composables/useLoginModal';
import { useRechargeModal } from '@/composables/useRechargeModal';
import { useIdentityCredits } from '@/composables/useIdentityCredits';
import { SettingOutlined } from '@ant-design/icons-vue';
import CreditsDisplay from './CreditsDisplay.vue';
import ModelSettingsModal from './ModelSettingsModal.vue';
import RechargeModal from './RechargeModal.vue';
import CreditsLogModal from './CreditsLogModal.vue';
import NotificationDropdown from './NotificationDropdown.vue';
import {
  BindAccountModal,
  clearBindUserInfo,
  ensureBindUserInfoLoaded,
  openBindAccountModal,
  useBindAccountState
} from '@/features/bind-account';
import { SITE_BRAND_NAME } from '@shared/constants/brand';

const { Header, Content } = Layout;

const router = useRouter();
const route = useRoute();
const userStore = useUserStore();
const { displayBalance } = useIdentityCredits();
const { goToLogin } = useLoginModal();
const { showRechargeModal, openRechargeModal } = useRechargeModal();
const { isPhoneBound, isWechatBound } = useBindAccountState();

// 当前选中的菜单项
const selectedKeys = ref<string[]>(['storyboard']);

// 移动端菜单抽屉状态
const mobileMenuVisible = ref(false);

// 群聊二维码弹窗状态
const groupChatModalVisible = ref(false);

// 右侧悬浮群聊按钮显隐（关闭后本次会话内隐藏，刷新后恢复）
const floatingGroupChatVisible = ref(true);

// 是否为移动端视图
const isMobile = ref(false);

// 监听窗口大小变化
const checkMobile = () => {
  isMobile.value = window.innerWidth < 768;
  // 切换到桌面端时关闭移动端菜单
  if (!isMobile.value) {
    mobileMenuVisible.value = false;
  }
};

onMounted(() => {
  checkMobile();
  window.addEventListener('resize', checkMobile);
  if (userStore.isLoggedIn) {
    void ensureBindUserInfoLoaded();
  }
});

onUnmounted(() => {
  window.removeEventListener('resize', checkMobile);
});

watch(() => userStore.isLoggedIn, (loggedIn) => {
  if (loggedIn) {
    void ensureBindUserInfoLoaded();
    return;
  }

  clearBindUserInfo();
}, { immediate: true });

// 监听路由变化，更新选中的菜单项
watch(
  () => route.path,
  (path) => {
    if (path.startsWith('/storyboard')) {
      selectedKeys.value = ['storyboard'];
    } else if (path.startsWith('/assets')) {
      selectedKeys.value = ['assets'];
    } else if (path.startsWith('/image-editor')) {
      selectedKeys.value = ['image-studio'];
    } else if (path.startsWith('/image-studio')) {
      selectedKeys.value = ['image-studio'];
    } else if (path.startsWith('/plaza')) {
      selectedKeys.value = ['plaza'];
    } else if (path.startsWith('/feedback')) {
      selectedKeys.value = ['feedback'];
    } else if (path.startsWith('/changelog')) {
      selectedKeys.value = ['changelog'];
    } else {
      selectedKeys.value = ['storyboard'];
    }
    // 路由变化时关闭移动端菜单
    mobileMenuVisible.value = false;
  },
  { immediate: true }
);

// 导航菜单配置
const menuItems = [
  {
    key: 'storyboard',
    icon: HomeOutlined,
    label: '故事版',
    path: '/storyboard'
  },
  {
    key: 'assets',
    icon: PictureOutlined,
    label: '素材库',
    path: '/assets'
  },
  {
    key: 'image-studio',
    icon: AppstoreOutlined,
    label: '图片生成',
    path: '/image-studio'
  },
  {
    key: 'plaza',
    icon: GlobalOutlined,
    label: '作品广场',
    path: '/plaza'
  },
  {
    key: 'feedback',
    icon: MessageOutlined,
    label: '意见反馈',
    path: '/feedback'
  },
  {
    key: 'changelog',
    icon: HistoryOutlined,
    label: '更新历史',
    path: '/changelog'
  },
];

/** 处理菜单点击 */
const handleMenuClick = ({ key }: { key: string | number }) => {
  const item = menuItems.find(m => m.key === String(key));
  if (item) {
    router.push(item.path);
    mobileMenuVisible.value = false;
  }
};

/** 切换移动端菜单 */
const toggleMobileMenu = () => {
  mobileMenuVisible.value = !mobileMenuVisible.value;
};

/** 处理用户菜单点击 */
const handleUserMenuClick = async ({ key }: { key: string | number }) => {
  const keyStr = String(key);
  switch (keyStr) {
    case 'profile':
      router.push('/user/center');
      break;
    case 'model-settings':
      modelSettingsOpen.value = true;
      break;
    case 'credits':
      openRechargeModal();
      break;
    case 'credits-log':
      creditsLogOpen.value = true;
      break;
    case 'works':
      router.push('/storyboard');
      break;
    case 'feedback':
      router.push('/feedback');
      break;
    case 'changelog':
      router.push('/changelog');
      break;
    case 'bind-phone':
      if (isPhoneBound.value) {
        message.info('当前账号已绑定手机号');
      } else {
        openBindAccountModal('phone');
      }
      break;
    case 'bind-wechat':
      if (isWechatBound.value) {
        message.info('当前账号已绑定微信');
      } else {
        openBindAccountModal('wechat');
      }
      break;
    case 'contact-service':
      window.open('https://kefu.chaoshidai.net/visitor/GfzO3cbOK93XA-_fQMuPg', '_blank');
      break;
    case 'join-group':
      groupChatModalVisible.value = true;
      break;
    case 'logout':
      await handleLogout();
      break;
  }
  mobileMenuVisible.value = false;
};

/** 处理登出 */
const handleLogout = async () => {
  try {
    userStore.logout();
    message.success('已退出登录');
    router.push('/storyboard');
  } catch (error) {
    console.error('登出失败:', error);
    // 即使出错，也清除本地状态
    userStore.logout();
    router.push('/storyboard');
  }
};

// 模型设置弹窗状态
const modelSettingsOpen = ref(false);

// 积分记录弹窗状态
const creditsLogOpen = ref(false);

/** 打开注册弹窗（注册模式） */
const goToRegister = () => {
  goToLogin({ register: true });
};

/** 创建新作品 */
const handleCreate = () => {
  router.push('/storyboard');
};
</script>

<template>
  <Layout class="main-layout">
    <!-- 顶部导航栏 -->
    <Header class="main-header">
      <div class="header-content">
        <!-- Logo -->
        <div class="logo" @click="router.push('/storyboard')">
          <AppstoreOutlined class="logo-icon" />
          <span class="logo-text">{{ SITE_BRAND_NAME }}</span>
        </div>

        <!-- 桌面端导航菜单 -->
        <Menu
          v-if="!isMobile"
          v-model:selected-keys="selectedKeys"
          mode="horizontal"
          theme="dark"
          class="nav-menu"
          @click="handleMenuClick"
        >
          <Menu.Item v-for="item in menuItems" :key="item.key">
            <component :is="item.icon" />
            <span>{{ item.label }}</span>
          </Menu.Item>
        </Menu>

        <!-- 右侧操作区 -->
        <div class="header-right">
          <!-- 桌面端：创建按钮 -->
          <Button v-if="!isMobile" type="primary" class="create-btn" @click="handleCreate">
            <template #icon>
              <PlusOutlined />
            </template>
            开始创作
          </Button>

          <!-- 移动端：创建按钮（简化版） -->
          <Button v-if="isMobile" type="primary" shape="circle" size="small" @click="handleCreate">
            <template #icon>
              <PlusOutlined />
            </template>
          </Button>

          <!-- 已登录状态 -->
          <template v-if="userStore.isLoggedIn">
            <!-- 桌面端：积分显示 -->
            <CreditsDisplay v-if="!isMobile" />

            <!-- 通知下拉 -->
            <NotificationDropdown v-if="!isMobile" />

            <!-- 用户头像下拉菜单 -->
            <Dropdown :trigger="['click']" placement="bottomRight">
              <div class="user-avatar">
                <Avatar :size="isMobile ? 28 : 32" :src="userStore.user?.avatar">
                  <template #icon>
                    <UserOutlined />
                  </template>
                </Avatar>
                <span v-if="!isMobile" class="user-name">{{ userStore.displayName }}</span>
              </div>
              <template #overlay>
                <Menu class="user-dropdown-menu" @click="handleUserMenuClick">
                  <!-- 用户信息头部 -->
                  <div class="user-info-header">
                    <Avatar :size="48" :src="userStore.user?.avatar">
                      <template #icon>
                        <UserOutlined />
                      </template>
                    </Avatar>
                    <div class="user-info-detail">
                      <div class="user-info-name">{{ userStore.displayName }}</div>
                      <div class="user-info-email">{{ userStore.user?.email || '未设置邮箱' }}</div>
                    </div>
                  </div>
                  <Menu.Divider />
                  <!-- 菜单项 -->
                  <Menu.Item key="profile">
                    <UserOutlined />
                    <span style="margin-left: 8px">个人中心</span>
                  </Menu.Item>
                  <Menu.Item key="works">
                    <AppstoreOutlined />
                    <span style="margin-left: 8px">我的作品</span>
                  </Menu.Item>
                  <Menu.Item key="bind-phone">
                    <MobileOutlined />
                    <span style="margin-left: 8px">{{ isPhoneBound ? '已绑定手机' : '绑定手机' }}</span>
                  </Menu.Item>
                  <Menu.Item key="bind-wechat">
                    <MessageOutlined />
                    <span style="margin-left: 8px">{{ isWechatBound ? '已绑定微信' : '绑定微信' }}</span>
                  </Menu.Item>
                  <Menu.Item key="model-settings">
                    <SettingOutlined />
                    <span style="margin-left: 8px">模型选项</span>
                  </Menu.Item>
                  <Menu.Item key="credits">
                    <WalletOutlined />
                    <span style="margin-left: 8px">我的积分</span>
                    <Badge 
                      :count="displayBalance >= 0 ? displayBalance : 0"
                      :overflow-count="9999" 
                      class="credits-badge" 
                    />
                  </Menu.Item>
                  <Menu.Item key="credits-log">
                    <OrderedListOutlined />
                    <span style="margin-left: 8px">积分记录</span>
                  </Menu.Item>
                  <Menu.Item key="feedback">
                    <MessageOutlined />
                    <span style="margin-left: 8px">意见反馈</span>
                  </Menu.Item>
                  <Menu.Item key="changelog">
                    <HistoryOutlined />
                    <span style="margin-left: 8px">更新历史</span>
                  </Menu.Item>
                  <Menu.Item key="contact-service">
                    <CustomerServiceOutlined />
                    <span style="margin-left: 8px">联系客服</span>
                  </Menu.Item>
                  <Menu.Item key="join-group">
                    <TeamOutlined />
                    <span style="margin-left: 8px">加入群聊</span>
                  </Menu.Item>
                  <Menu.Divider />
                  <Menu.Item key="logout" danger>
                    <LogoutOutlined />
                    <span style="margin-left: 8px">退出登录</span>
                  </Menu.Item>
                </Menu>
              </template>
            </Dropdown>
          </template>

          <!-- 未登录状态 -->
          <template v-else>
            <Space :size="isMobile ? 4 : 8">
              <Button type="text" class="login-btn" :size="isMobile ? 'small' : 'middle'" @click="goToLogin()">
                登录
              </Button>
              <Button type="primary" class="register-btn" :size="isMobile ? 'small' : 'middle'" @click="goToRegister">
                注册
              </Button>
            </Space>
          </template>

          <!-- 移动端：汉堡菜单按钮 -->
          <Button 
            v-if="isMobile" 
            type="text" 
            class="mobile-menu-btn"
            @click="toggleMobileMenu"
          >
            <MenuOutlined v-if="!mobileMenuVisible" />
            <CloseOutlined v-else />
          </Button>
        </div>
      </div>
    </Header>

    <!-- 移动端导航抽屉 -->
    <Drawer
      v-model:open="mobileMenuVisible"
      placement="right"
      :width="280"
      :closable="false"
      class="mobile-drawer"
    >
      <div class="mobile-menu-content">
        <!-- 导航菜单 -->
        <Menu
          v-model:selected-keys="selectedKeys"
          mode="vertical"
          theme="dark"
          class="mobile-nav-menu"
          @click="handleMenuClick"
        >
          <Menu.Item v-for="item in menuItems" :key="item.key">
            <component :is="item.icon" />
            <span style="margin-left: 8px">{{ item.label }}</span>
          </Menu.Item>
        </Menu>

        <!-- 移动端积分显示（已登录时） -->
        <div v-if="userStore.isLoggedIn" class="mobile-credits" @click="openRechargeModal">
          <WalletOutlined />
          <span>我的积分：</span>
          <span class="credits-value">{{ displayBalance >= 0 ? displayBalance : '--' }}</span>
        </div>
      </div>
    </Drawer>

    <!-- 主内容区域 -->
    <Content class="main-content">
      <slot />
    </Content>

    <!-- 模型设置弹窗 -->
    <ModelSettingsModal v-model:open="modelSettingsOpen" />
    
    <!-- 充值弹窗 -->
    <RechargeModal v-model:open="showRechargeModal" />

    <!-- 积分记录弹窗 -->
    <CreditsLogModal v-model:open="creditsLogOpen" />

    <!-- 账号绑定弹窗 -->
    <BindAccountModal />

    <!-- 群聊二维码弹窗 -->
    <Modal
      v-model:open="groupChatModalVisible"
      title="加入群聊"
      :footer="null"
      centered
      width="320px"
    >
      <div class="group-chat-modal">
        <img src="/qunliao.png" alt="群聊二维码" class="qr-code" />
        <p class="tip">打开微信扫一扫，加入用户交流群</p>
      </div>
    </Modal>

    <!-- 右侧悬浮加入群聊按钮 -->
    <div v-if="floatingGroupChatVisible" class="floating-group-chat">
      <button class="floating-close" @click.stop="floatingGroupChatVisible = false">×</button>
      <div class="floating-body" @click="groupChatModalVisible = true">
        <TeamOutlined class="floating-icon" />
        <span class="floating-text">加入群聊</span>
      </div>
    </div>
  </Layout>
</template>

<style lang="scss" scoped>
.group-chat-modal {
  text-align: center;
  padding: 16px 0;
  
  .qr-code {
    width: 200px;
    height: 200px;
    border-radius: 8px;
  }
  
  .tip {
    margin-top: 16px;
    color: var(--text-secondary);
    font-size: 14px;
  }
}

/* 右侧悬浮加入群聊按钮 */
.floating-group-chat {
  position: fixed;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  z-index: 99;
  display: flex;
  flex-direction: column;
  align-items: flex-end;

  .floating-close {
    position: absolute;
    top: 0;
    right: 0;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: rgba(0, 0, 0, 0.45);
    border: none;
    color: #fff;
    font-size: 12px;
    line-height: 1;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 0.2s;
    z-index: 1;

    &:hover {
      background: rgba(0, 0, 0, 0.75);
    }
  }

  .floating-body {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 44px;
    padding: 12px 0;
    background: linear-gradient(135deg, #277be8 0%, #1e5bb8 100%);
    border-radius: 8px 0 0 8px;
    cursor: pointer;
    box-shadow: -2px 0 12px rgba(39, 123, 232, 0.3);
    transition: all 0.25s ease;

    &:hover {
      width: 52px;
      box-shadow: -4px 0 16px rgba(39, 123, 232, 0.45);

      .floating-icon {
        transform: scale(1.1);
      }
    }
  }

  .floating-icon {
    font-size: 20px;
    color: #fff;
    transition: transform 0.2s ease;
  }

  .floating-text {
    writing-mode: vertical-rl;
    text-orientation: mixed;
    font-size: 12px;
    color: #fff;
    margin-top: 8px;
    letter-spacing: 2px;
    font-weight: 500;
  }
}

/* 移动端隐藏悬浮按钮 */
@media (max-width: 768px) {
  .floating-group-chat {
    display: none;
  }
}

.main-layout {
  min-height: 100vh;
  background-color: var(--bg-primary);
}

.main-header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  height: var(--header-height);
  padding: 0;
  background-color: #141414; // 更深的背景色
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  backdrop-filter: blur(8px);
}

.header-content {
  display: flex;
  align-items: center;
  height: 100%;
  width: 100%;
  max-width: 1600px;
  margin: 0 auto;
  padding: 0 32px;
}

.logo {
  display: flex;
  align-items: center;
  cursor: pointer;
  margin-right: 40px;
  flex-shrink: 0;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.85;
  }

  &-icon {
    font-size: 26px;
    color: #277be8; // 蓝色品牌色
    filter: drop-shadow(0 0 8px rgba(39, 123, 232, 0.3));
  }

  &-text {
    margin-left: 10px;
    font-size: 18px;
    font-weight: 700;
    color: #ffffff;
    letter-spacing: 0.5px;
  }
}

.nav-menu {
  flex: 1;
  background: transparent;
  border-bottom: none;
  line-height: var(--header-height);

  :deep(.ant-menu-item) {
    position: relative;
    color: rgba(255, 255, 255, 0.65);
    font-size: 14px;
    padding: 0 20px;
    margin: 0 4px;
    transition: color 0.2s;
    
    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 50%;
      transform: translateX(-50%);
      width: 0;
      height: 2px;
      background: linear-gradient(90deg, #277be8, #1e5bb8);
      border-radius: 1px;
      transition: width 0.25s ease;
    }

    &:hover {
      color: #ffffff;
      background-color: transparent;
      
      &::after {
        width: 24px;
      }
    }

    &.ant-menu-item-selected {
      color: #ffffff;
      background-color: transparent;
      
      &::after {
        width: 24px;
      }
    }
  }

  :deep(.ant-menu-horizontal) {
    border-bottom: none;
  }
}

.header-right {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-left: auto;
}

.create-btn {
  height: 34px;
  padding: 0 20px;
  border-radius: 17px;
  font-size: 14px;
  font-weight: 500;
  background: linear-gradient(135deg, #277be8 0%, #1e5bb8 100%);
  border: none;
  box-shadow: 0 2px 8px rgba(39, 123, 232, 0.25);
  transition: all 0.25s ease;

  &:hover {
    background: linear-gradient(135deg, #3a8cf0 0%, #277be8 100%);
    box-shadow: 0 4px 12px rgba(39, 123, 232, 0.35);
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
}

.credits-display {
  display: flex;
  align-items: center;
  padding: 6px 12px;
  background: transparent;
  border: none;
  border-radius: 20px;
  color: rgba(255, 255, 255, 0.85);
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s ease;

  .anticon {
    color: #277be8;
    font-size: 15px;
  }

  .credits-value {
    margin-left: 6px;
    color: #ffc53d;
    font-weight: 600;
    font-size: 14px;
  }
}

.user-avatar {
  display: flex;
  align-items: center;
  cursor: pointer;
  padding: 4px 8px 4px 4px;
  border-radius: 22px;
  background: transparent;
  border: none;
  transition: all 0.2s ease;

  :deep(.ant-avatar) {
    border: 2px solid rgba(39, 123, 232, 0.4);
    transition: all 0.25s ease;
  }

  &:hover {
    :deep(.ant-avatar) {
      border-color: #277be8;
      box-shadow: 0 0 12px rgba(39, 123, 232, 0.5);
    }

    .user-name {
      color: #ffffff;
    }
  }

  .user-name {
    margin-left: 8px;
    color: rgba(255, 255, 255, 0.9);
    font-size: 13px;
    font-weight: 500;
    max-width: 80px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    transition: color 0.2s ease;
  }
}

.login-btn {
  color: rgba(255, 255, 255, 0.7);
  font-size: 14px;
  padding: 4px 12px;
  height: 34px;
  border-radius: 17px;
  transition: all 0.2s;

  &:hover {
    color: #ffffff;
    background-color: rgba(255, 255, 255, 0.08);
  }
}

.register-btn {
  height: 34px;
  padding: 0 18px;
  border-radius: 17px;
  font-size: 14px;
  background-color: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.15);
  color: #ffffff;
  transition: all 0.2s;

  &:hover {
    background-color: rgba(255, 255, 255, 0.15);
    border-color: rgba(255, 255, 255, 0.2);
  }
}

/* 移动端汉堡菜单按钮 */
.mobile-menu-btn {
  color: rgba(255, 255, 255, 0.8);
  font-size: 18px;
  padding: 6px 10px;
  border-radius: 8px;
  
  &:hover {
    color: #ffffff;
    background-color: rgba(255, 255, 255, 0.08);
  }
}

/* 用户下拉菜单样式 */
.user-dropdown-menu {
  min-width: 220px;
  background-color: #1f1f1f;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  padding: 8px 0;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);

  .user-info-header {
    display: flex;
    align-items: center;
    padding: 14px 16px;
    gap: 12px;

    .user-info-detail {
      flex: 1;
      overflow: hidden;

      .user-info-name {
        font-size: 14px;
        font-weight: 600;
        color: #ffffff;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .user-info-email {
        font-size: 12px;
        color: rgba(255, 255, 255, 0.45);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        margin-top: 3px;
      }
    }
  }

  :deep(.ant-dropdown-menu-item) {
    padding: 10px 16px;
    color: rgba(255, 255, 255, 0.75);
    border-radius: 0;
    margin: 0 8px;
    border-radius: 6px;

    &:hover {
      background-color: rgba(255, 255, 255, 0.06);
      color: #ffffff;
    }

    &.ant-dropdown-menu-item-danger {
      color: #ff6b6b;

      &:hover {
        background-color: rgba(255, 107, 107, 0.1);
      }
    }
  }

  :deep(.ant-dropdown-menu-item-divider) {
    background-color: rgba(255, 255, 255, 0.06);
    margin: 8px 16px;
  }

  .credits-badge {
    margin-left: auto;
    
    :deep(.ant-badge-count) {
      background-color: #ffd666;
      color: #000;
      font-size: 11px;
      min-width: 32px;
      font-weight: 600;
    }
  }
}

.main-content {
  margin-top: var(--header-height);
  min-height: calc(100vh - var(--header-height));
  background-color: var(--bg-primary);
}

/* 移动端抽屉样式 */
.mobile-drawer {
  :deep(.ant-drawer-content) {
    background-color: #141414;
  }
  
  :deep(.ant-drawer-body) {
    padding: 0;
    background-color: #141414;
  }
}

.mobile-menu-content {
  padding: 16px 0;
}

.mobile-nav-menu {
  background: transparent;
  border: none;
  
  :deep(.ant-menu-item) {
    height: 52px;
    line-height: 52px;
    margin: 4px 12px;
    padding: 0 16px;
    color: rgba(255, 255, 255, 0.65);
    border-radius: 8px;
    
    &:hover {
      color: #ffffff;
      background-color: rgba(255, 255, 255, 0.06);
    }
    
    &.ant-menu-item-selected {
      color: #ffffff;
      background-color: rgba(39, 123, 232, 0.2);
    }
  }
}

.mobile-credits {
  display: flex;
  align-items: center;
  padding: 16px 24px;
  margin: 16px 12px 0;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.65);
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.06);
  }
  
  .credits-value {
    margin-left: 4px;
    color: #ffd666;
    font-weight: 600;
  }
}

/* 移动端响应式适配 */
@media (max-width: 768px) {
  .header-content {
    padding: 0 16px;
  }
  
  .logo {
    margin-right: 0;
    
    &-icon {
      font-size: 22px;
    }
    
    &-text {
      font-size: 15px;
    }
  }
  
  .header-right {
    gap: 8px;
  }
  
  .create-btn {
    height: 30px;
    padding: 0 14px;
    font-size: 13px;
  }
}

@media (max-width: 480px) {
  .logo {
    &-text {
      display: none; /* 极小屏幕隐藏文字，只显示图标 */
    }
  }
  
  .header-content {
    padding: 0 12px;
  }
}
</style>
